/* istanbul ignore file */

import React, { useEffect, Fragment, useState } from 'react';
import { IntlProvider, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { RadioButton, RadioGroup } from '@americanexpress/dls-react';
import { IconInfo } from '@americanexpress/dls-icons';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { Helmet } from 'react-helmet';
import DocUploadAlwaysKyc from './flows/DocUploadAlwaysKyc';
import DocUploadNeverKyc from './flows/DocUploadNeverKyc';
import './DocUploadStyles.scss';

const KYC_FLOW = {
  NEVER: 'NEVER_KYC',
  ALWAYS: 'ALWAYS_KYC',
  SOMETIMES: 'SOMETIMES_KYC',
};

function DocUpload(props) {
  const {
    maxFileSize,
    maxBatchSize,
    maxSizeLabel,
    maxBatchSizeLabel,
    width,
    fileTypes,
    fileTypesLabel,
    isSubmitDisabled,
    isUploadDisabled,
    submitPopLabel,
    uploadZoneView,
    fileReqView,
    docListView,
    sizeBarView,
    submitView,
    onStartUploadCall,
    onUploadCall,
    onDeleteCall,
    onSubmitCall,
    filePreview,
    downloadProgress,
    errorLabel,
    altSubmit,
    formDataProps,
    languageData,
    kycFlow,
    kycWidth,
    idTypeMetadataApi,
    kycDocumentMetadataApi,
  } = props;

  const dispatch = useDispatch();
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));
  const docUploadUrl = useSelector((state) => state.getIn(['config', 'imageViewerDocUploadApiUrl']));
  const [sometimesKycSelection, setSometimesKycSelection] = useState('');

  useEffect(() => {
    dispatch(loadLanguagePack('axp-document-uploader', { fallbackLocale: 'en-US' }));
  }, [dispatch, locale]);

  const resolveFlowVariant = (flow) => {
    const normalizedFlow = typeof flow === 'string' ? flow.trim().toUpperCase() : KYC_FLOW.NEVER;

    if (normalizedFlow === KYC_FLOW.ALWAYS) {
      return KYC_FLOW.ALWAYS;
    }
    if (normalizedFlow === KYC_FLOW.SOMETIMES) {
      return KYC_FLOW.SOMETIMES;
    }
    return KYC_FLOW.NEVER;
  };

  const flowVariant = resolveFlowVariant(kycFlow);

  const renderSometimesKycQuestion = () => (
    <div className="doc-upload-width-shell duak-outer-container dusk-sometimes-kyc-container dls-white-bg flex flex-column flex-align-start">
      <div className="dusk-sometimes-kyc-question label dls-grey-03 font-weight-medium">
        <FormattedMessage id="sometimesKycQuestion" defaultMessage="Are you uploading verification documents?" />
      </div>
      <div className="dusk-sometimes-kyc-helper flex flex-justify-center flex-align-center">
        <div className="dusk-sometimes-kyc-helper-icon-container flex flex-justify-center flex-align-center margin-1-r">
          <IconInfo size="sm" color="neutral" title="information icon" />
        </div>
        <div className="dusk-sometimes-kyc-helper-text dls-grey-04 font-weight-normal">
          <FormattedMessage
            id="sometimesKycHelper"
            defaultMessage="Verification documents may include proof of identity, proof of address, or proof of income / source of funds."
          />
        </div>
      </div>
      <RadioGroup
        legend={<FormattedMessage id="sometimesKycQuestion" defaultMessage="Are you uploading verification documents?" />}
        isLegendVisuallyHidden={true}
        orientation="vertical"
        value={sometimesKycSelection}
        onChange={(event) => setSometimesKycSelection(event.target.value)}
        className="dusk-sometimes-kyc-radio-group width-full"
      >
        <RadioButton
          id="sometimes-kyc-selection-yes"
          value="yes"
          label={<FormattedMessage id="sometimesKycYes" defaultMessage="Yes" />}
        />
        <RadioButton
          id="sometimes-kyc-selection-no"
          value="no"
          label={<FormattedMessage id="sometimesKycNo" defaultMessage="No" />}
        />
      </RadioGroup>
    </div>
  );

  const renderFlow = () => {
    const renderNeverKycFlow = () => (
      <DocUploadNeverKyc
        maxFileSize={maxFileSize}
        maxBatchSize={maxBatchSize}
        maxSizeLabel={maxSizeLabel}
        maxBatchSizeLabel={maxBatchSizeLabel}
        width={width}
        fileTypes={fileTypes}
        fileTypesLabel={fileTypesLabel}
        isSubmitDisabled={isSubmitDisabled}
        isUploadDisabled={isUploadDisabled}
        submitPopLabel={submitPopLabel}
        uploadZoneView={uploadZoneView}
        fileReqView={fileReqView}
        docListView={docListView}
        sizeBarView={sizeBarView}
        submitView={submitView}
        onStartUploadCall={onStartUploadCall}
        onUploadCall={onUploadCall}
        onDeleteCall={onDeleteCall}
        onSubmitCall={onSubmitCall}
        filePreview={filePreview}
        downloadProgress={downloadProgress}
        errorLabel={errorLabel}
        altSubmit={altSubmit}
        formDataProps={formDataProps}
        docUploadUrl={docUploadUrl}
      />
    );

    const renderAlwaysKycFlow = () => (
      <DocUploadAlwaysKyc
        maxFileSize={maxFileSize}
        maxBatchSize={maxBatchSize}
        maxSizeLabel={maxSizeLabel}
        maxBatchSizeLabel={maxBatchSizeLabel}
        width={kycWidth}
        fileTypes={fileTypes}
        fileTypesLabel={fileTypesLabel}
        isSubmitDisabled={isSubmitDisabled}
        isUploadDisabled={isUploadDisabled}
        submitPopLabel={submitPopLabel}
        uploadZoneView={uploadZoneView}
        fileReqView={fileReqView}
        docListView="ViewProofKyc"
        submitView={submitView}
        onStartUploadCall={onStartUploadCall}
        onUploadCall={onUploadCall}
        onDeleteCall={onDeleteCall}
        onSubmitCall={onSubmitCall}
        filePreview={filePreview}
        downloadProgress={downloadProgress}
        errorLabel={errorLabel}
        altSubmit={altSubmit}
        formDataProps={formDataProps}
        docUploadUrl={docUploadUrl}
        idTypeMetadataApi={idTypeMetadataApi}
        kycDocumentMetadataApi={kycDocumentMetadataApi}
      />
    );

    switch (flowVariant) {
      case KYC_FLOW.ALWAYS:
        return renderAlwaysKycFlow();
      case KYC_FLOW.SOMETIMES: {
        let selectedFlow = null;
        if (sometimesKycSelection === 'yes') {
          selectedFlow = renderAlwaysKycFlow();
        } else if (sometimesKycSelection === 'no') {
          selectedFlow = renderNeverKycFlow();
        }

        return (
          <div className="dusk-sometimes-kyc-flow-stack flex flex-column flex-align-start">
            {renderSometimesKycQuestion()}
            {selectedFlow}
          </div>
        );
      }
      default:
        return renderNeverKycFlow();
    }
  };

  return (
    <Fragment>
      <Helmet
        {...{
          link: [
            {
              rel: 'stylesheet',
              href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.17.0/stylesheets/dls-core.min.css',
            },
          ],
        }}
      />

      <IntlProvider locale={locale} messages={languageData}>
        {renderFlow()}
      </IntlProvider>
    </Fragment>
  );
}

DocUpload.defaultProps = {
  maxFileSize: 30000000,
  maxBatchSize: 100000000,
  maxSizeLabel: '30 MB',
  maxBatchSizeLabel: '100 MB',
  width: '69rem',
  fileTypesLabel: '',
  isSubmitDisabled: false,
  isUploadDisabled: false,
  submitPopLabel: null,
  uploadZoneView: 'ViewOne',
  fileReqView: 'ViewOne',
  docListView: 'ViewOne',
  sizeBarView: 'ViewOne',
  submitView: 'ViewOne',
  onStartUploadCall: null,
  onUploadCall: null,
  onDeleteCall: null,
  onSubmitCall: null,
  filePreview: null,
  downloadProgress: null,
  errorLabel: null,
  altSubmit: null,
  kycFlow: 'NEVER_KYC',
  kycWidth: '69rem',
  languageData: {},
  formDataProps: {
    accountNumber: '734739227382738',
    market: 'UK',
    consumerId: 'MERESIGN',
    processId: 'merchant_uk_branch_addition_uk_english',
    se_number: '1234567890',
    language: 'English',
    case_no: '987654321',
  },
};

DocUpload.propTypes = {
  fileTypes: PropTypes.string.isRequired,
  fileTypesLabel: PropTypes.string,
  maxFileSize: PropTypes.number,
  maxBatchSize: PropTypes.number,
  maxSizeLabel: PropTypes.string,
  maxBatchSizeLabel: PropTypes.string,
  width: PropTypes.string,
  isSubmitDisabled: PropTypes.bool,
  isUploadDisabled: PropTypes.bool,
  uploadZoneView: PropTypes.string,
  fileReqView: PropTypes.string,
  docListView: PropTypes.string,
  sizeBarView: PropTypes.string,
  submitView: PropTypes.string,
  formDataProps: PropTypes.instanceOf(Object),
  submitPopLabel: PropTypes.string,
  onStartUploadCall: PropTypes.func,
  onUploadCall: PropTypes.func,
  onDeleteCall: PropTypes.func,
  onSubmitCall: PropTypes.func,
  filePreview: PropTypes.func,
  downloadProgress: PropTypes.instanceOf(Object),
  errorLabel: PropTypes.string,
  altSubmit: PropTypes.func,
  kycFlow: PropTypes.oneOf([
    'NEVER_KYC',
    'ALWAYS_KYC',
    'SOMETIMES_KYC',
  ]),
  kycWidth: PropTypes.string,
  languageData: PropTypes.instanceOf(Object),
  idTypeMetadataApi: PropTypes.func,
  kycDocumentMetadataApi: PropTypes.func,
};

export const loadModuleData = async ({ store: { dispatch } }) => {
  await dispatch(loadLanguagePack('axp-document-uploader', { fallbackLocale: 'en-US' }));
};

DocUpload.holocron = {
  name: 'axp-document-uploader',
  loadModuleData,
};

const UnwrappedDocUpload = DocUpload;
export { UnwrappedDocUpload };

// export default hocChain(DocUploadForm);
export default compose(
  oneAppModuleWrapper('axp-document-uploader', { loadModuleData })
)(DocUpload);
